import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import { useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { v4 } from 'uuid';
import { RequestParameters } from '~/backend/backend-client';
import { Modality } from '~/domain/dicom/modality';
import { getDatasetEnvironmentRegistry } from '~/engine/scene/datasetEnvironmentRegistryInstance';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { ensureStructureSetsCurrent } from '~/hooks/ensureStructureSetsCurrent';
import { dosePredictionTaskState } from '~/state/atoms/dose/dosePredictionTaskAtom';
import { OptimizationObjective } from '~/domain/patient/optimizationObjective';
import { Job } from '~/state/atoms/jobAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import {
    WorkflowTypes,
    pseudonymizeState,
    workflowModalState,
} from '~/state/atoms/viewerAtoms';
import { handleError } from '~/utils/errors';
import {
    getHighestDoseQuantity,
    transformDoseTargets,
} from '~/utils/transforms/doseTargetsTransforms';
import { splitDicomAndJsonFiles } from '~/utils/io/files/FileHandler';
import {
    extractOptimizationObjectivesFile,
    readOptimizationObjectives,
} from '~/utils/transforms/optimizationObjectivesTransforms';
import { transformTaskToGenerationModel } from '~/utils/transforms/taskTransforms';
import { useApplyPredictedDoseSettings } from '~/hooks/dose/useApplyPredictedDoseSettings';
import useProcessDicomFiles from '../../../useProcessDicomFiles';
import useConfirmedSelection from '~/hooks/datasetSelection/useConfirmedSelection';
import { NewJobData, useJobManagement } from '../../useJobManagement';
import useAnonymizer from '../useAnonymizer';
import { useDoseBackend, useSessionID } from '../useBackend';
import { optimizationObjectivesState } from '~/state/atoms/dose/optimizationObjectivesAtom';
import { useSelectStep } from '~/routes/workflowStepParam';

/**
 * A React hook for creating a dose prediction task.
 * @returns {Object} - An object containing startProcessing function and submission state.
 */
export function useDosePrediction() {
    const primaryImage = useSelectedPrimaryImage();
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const pseudonymize = useRecoilValue(pseudonymizeState);
    const setWorkflowModalState = useSetRecoilState(workflowModalState);
    const dosePredictionTask = useRecoilValue(dosePredictionTaskState);
    const session = useSessionOrNull();
    const predictObjectives = true; // hardcoded to as agreed for release, can be made dynamic in the future if needed
    const [isSubmitting, setIsSubmitting] = useState(false);
    const selectStep = useSelectStep();

    const doseClient = useDoseBackend();
    const { addJob, updateJob } = useJobManagement();
    const { anonymizeBlobs, reidentifyFiles } = useAnonymizer();
    const { resetSessionID, sessionID } = useSessionID();
    const { processCreatedFiles } = useProcessDicomFiles();
    const { pickDose } = useConfirmedSelection();
    const applyPredictedDoseSettings = useApplyPredictedDoseSettings();
    const setOptimizationObjectives = useSetRecoilState(
        optimizationObjectivesState
    );
    const setDosePredictionTask = useSetRecoilState(dosePredictionTaskState);

    const handleProcessedFiles = async (job: Job) => {
        if (job.results && job.results?.length > 0) {
            // Split non-DICOM JSON files from DICOM files up front so the
            // anonymizer/re-identifier never receives JSON bytes. Any JSON
            // file the worker returns (optimization objectives, metadata, etc.)
            // would cause reidentifyFiles to throw and return null, silently
            // dropping the entire dose result.
            const { dicomFiles: dicomResults, jsonFiles } =
                splitDicomAndJsonFiles(job.results);
            const objFile = extractOptimizationObjectivesFile(jsonFiles);

            const finalDicomFiles = job.payload?.pseudonymized
                ? await reidentifyFiles(dicomResults)
                : dicomResults;
            if (finalDicomFiles) {
                let objectives: OptimizationObjective[] = [];
                if (objFile) {
                    try {
                        objectives = await readOptimizationObjectives(objFile);
                    } catch (e) {
                        handleError(
                            e,
                            'Failed to parse optimization objectives.'
                        );
                    }
                }

                const generationModel = transformTaskToGenerationModel(
                    job.task
                );
                const processedItems = await processCreatedFiles(
                    finalDicomFiles,
                    generationModel
                );
                if (processedItems) {
                    updateJob(job.id, { isImported: true });
                    const taskTargets =
                        job.task && 'targets' in job.task
                            ? job.task.targets
                            : undefined;
                    applyPredictedDoseSettings(processedItems.doses, {
                        objectives,
                        targets: taskTargets,
                        normalizationValueGy: taskTargets
                            ? getHighestDoseQuantity(taskTargets)
                            : undefined,
                    });

                    // The result loads through the same question as a picked
                    // dose: the user may have changed the set while the job ran.
                    if (processedItems.doses[0]) {
                        pickDose(processedItems.doses[0]);
                    }
                }
            } else {
                handleError('Re-identification failed for one or more files.');
            }
        }
    };

    const startProcessing = async (modelName: string) => {
        if (isSubmitting) {
            return;
        }

        if (primaryImage && currentStructureSet) {
            const modality = primaryImage.seriesMetadata.modality;
            const isValidModality =
                modality === Modality.CT || modality === Modality.MR;
            const isTilted = primaryImage.firstSlice.tilted;
            const isHfs = primaryImage.firstSlice.patientPosition === 'HFS';
            if (!isValidModality || isTilted || !isHfs) {
                const reasons: string[] = [];
                if (!isValidModality) {
                    reasons.push(
                        `unsupported modality ${modality} (expected CT or MR)`
                    );
                }
                if (isTilted) {
                    reasons.push('gantry tilt is present');
                }
                if (!isHfs) {
                    reasons.push('patient position is not HFS');
                }
                const errorMessage = `The selected image is not supported: ${reasons.join(', ')}.`;
                handleError(new Error(errorMessage), errorMessage);
                return;
            }

            setIsSubmitting(true);
            try {
                resetSessionID();
                const taskUID = `${sessionID}.${v4().split('-')[0]}`;

                if (
                    !ensureStructureSetsCurrent(
                        getDatasetEnvironmentRegistry(),
                        [currentStructureSet],
                        'Dose prediction'
                    )
                ) {
                    return;
                }
                const blobs = Array.from(primaryImage.getSlicesAsDicomBlobs());

                if (!session) {
                    throw new Error(
                        'No active patient session for dose prediction.'
                    );
                }
                blobs.push(
                    session.structureSetExportService.exportBlob(
                        currentStructureSet.dicomUid
                    )
                );
                const blobsToUpload = pseudonymize
                    ? await anonymizeBlobs(taskUID, blobs)
                    : blobs;

                const backendParameters: RequestParameters = {
                    action: modelName,
                    taskUID,
                    nExpectedFiles: blobs.length.toString(),
                };

                if (
                    dosePredictionTask?.targets &&
                    dosePredictionTask.targets.length > 0
                ) {
                    backendParameters.targets = JSON.stringify({
                        unit: 'Gy',
                        rois: transformDoseTargets(dosePredictionTask.targets),
                    });
                }

                backendParameters.predict_objectives =
                    String(predictObjectives);

                if (blobsToUpload) {
                    const newJobDetails: NewJobData = {
                        id: sessionID,
                        name: 'Dose+',
                        totalItems: blobsToUpload.length,
                        type: WorkflowTypes.DOSE,
                        client: doseClient,
                        onLoad: handleProcessedFiles,
                        payload: {
                            filesToUpload: blobsToUpload.map((file) => ({
                                file,
                            })),
                            backendParameters,
                            pseudonymized: pseudonymize,
                        },
                        task: {
                            model: dosePredictionTask.model,
                            targets: dosePredictionTask.targets,
                        },
                        inputApprovalStatus: currentStructureSet.approvalStatus,
                    };

                    addJob(newJobDetails);
                    setWorkflowModalState((state) => ({
                        ...state,
                        open: false,
                    }));
                    selectStep('predictive-planning');
                } else {
                    handleError('Anonymization failed for one or more files.');
                }
            } catch (error) {
                handleError(error);
                setDosePredictionTask((prev) => ({
                    ...prev,
                    targets: undefined,
                    model: undefined,
                }));
                setOptimizationObjectives([]);
            } finally {
                setIsSubmitting(false);
            }
        } else {
            handleError('Image or structure set is missing!');
        }
    };

    return { startProcessing, isSubmitting };
}
